import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { StaffForm } from 'app/components/staff/StaffForm'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useInitialTableSort } from 'app/hooks/useInitialTableSort'
import { StaffJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import type { SearchResponse } from 'app/services/search'
import { useUser } from 'app/utils'
import { logError } from 'lib/logging'
import { gqlFetchList } from 'app/lib/graphqlClient'

export type StaffMember = StaffJson

export type UseStaffReturn = {
  staff: SearchResponse<StaffJson> | undefined
  canManageStaff: boolean
  initialSort: ReturnType<typeof useInitialTableSort>
  openCreateModal: () => void
  openEditModal: (staffMember: any) => void
}

export function useStaff(): UseStaffReturn {
  const user = useUser()
  const [searchParams] = useSearchParams()
  const checkPermission = useCheckPermission()
  const sort = searchParams.get(`sort`) === `` ? undefined : (searchParams.get(`sort`) ?? `name.keyword:asc`)
  const [staff, setStaff] = useState<SearchResponse<StaffJson>>()
  const initialSort = useInitialTableSort(sort)

  const canManageStaff = user?.type === `super` || checkPermission([`staff:manage`])

  useEffect(() => {
    const sort = searchParams.get(`sort`) === `` ? undefined : (searchParams.get(`sort`) ?? `name.keyword:asc`)
    const search = searchParams.get(`search`) ?? ``
    const sortParts = sort?.split(`:`) ?? []
    gqlFetchList<StaffJson>(
      'staffMembers',
      {
        search: search || undefined,
        sort: sortParts.length === 2 ? { field: sortParts[0], direction: sortParts[1] } : undefined,
      },
      'id name aliases userId',
    )
      .then((data) => {
        setStaff({ hits: data.hits as any, total: data.total, page: data.page })
      })
      .catch((err) => logError(`Error fetching staff`, err))
  }, [searchParams])

  const openModal = useCallback(
    ({ title, staff: staffMember }: { title: string; staff?: any }) => {
      const excludeUserIds =
        staff?.hits?.filter((s) => s.userId && s.id !== staffMember?.id)?.map((s) => s.userId!) || []

      return showModal(
        <StaffForm
          staff={staffMember}
          excludeUserIds={excludeUserIds}
          onSubmit={(data) => {
            if (data.staff && !staffMember) {
              setStaff((prev) =>
                prev ? { ...prev, hits: [data.staff as any, ...prev.hits], total: prev.total + 1 } : prev,
              )
            } else if (data.staff && staffMember) {
              setStaff((prev) =>
                prev
                  ? { ...prev, hits: prev.hits.map((s) => (s.id === data.staff!.id ? (data.staff as any) : s)) }
                  : prev,
              )
            }
          }}
          onDelete={() => {
            if (staffMember?.id) {
              setStaff((prev) =>
                prev
                  ? { ...prev, hits: prev.hits.filter((s) => s.id !== staffMember.id), total: prev.total - 1 }
                  : prev,
              )
            }
          }}
        />,
        { title, isFullScreen: false, removePadding: true, useRadix: true },
      )
    },
    [staff?.hits],
  )

  const openCreateModal = useCallback(() => openModal({ title: `Add Staff` }), [openModal])
  const openEditModal = useCallback(
    (staffMember: any) => openModal({ title: `Edit Staff`, staff: staffMember }),
    [openModal],
  )

  return { staff, canManageStaff, initialSort, openCreateModal, openEditModal }
}
